import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import noop from 'lodash/noop';
import { ReviewFragment } from '../../__generated__/Reviews';
import { REVIEW_IMAGE_GALLERY_EVENT_CATEGORY, trackInteractionEvent } from '../../../../analytics';
import { ReviewPhotos } from './util';
import useReviewPhotos from './useReviewPhotos';

type FetchMore = (incrementCount?: boolean) => void;
type SelectReview = (id: string) => void;

export const ReviewPhotoContext = createContext<{
  currentReviewPhotoIndex: number;
  setCurrentReviewEdgeIndex: Dispatch<SetStateAction<number>>;
  fetchMore: FetchMore;
  getNextReview: () => void;
  getPrevReview: () => void;
  hasMoreImages: boolean;
  hasMoreReviews: boolean;
  images: ReviewPhotos[];
  loading: boolean;
  onFirstReview: boolean;
  selectReview: SelectReview;
  showAllPhotosModal: boolean;
  setShowAllPhotosModal: Dispatch<SetStateAction<boolean>>;
  showReviewModal: boolean;
  setShowReviewModal: Dispatch<SetStateAction<boolean>>;
  currentReview: ReviewFragment | null;
  showReviewSheetStepped: boolean;
  setShowReviewSheetStepped: Dispatch<SetStateAction<boolean>>;
}>({
  currentReviewPhotoIndex: 0,
  setCurrentReviewEdgeIndex: noop,
  fetchMore: noop,
  getNextReview: noop,
  getPrevReview: noop,
  hasMoreImages: true,
  hasMoreReviews: true,
  images: [],
  loading: false,
  onFirstReview: false,
  selectReview: noop,
  showAllPhotosModal: false,
  setShowAllPhotosModal: noop,
  showReviewModal: false,
  setShowReviewModal: noop,
  currentReview: null,
  showReviewSheetStepped: false,
  setShowReviewSheetStepped: noop,
});

/**
 * Questions and Answers Provider
 * A context provider for storing/setting Q&A state so that each component in the tree can access it as needed.
 *
 * @return {TSX.Element}
 */
export const ReviewPhotoProvider: React.FC<{ children: React.ReactNode; entryID: string }> = ({
  children,
  entryID: id,
}) => {
  const {
    images,
    fetchMore,
    hasMoreImages,
    loading,
    query: { data, ...query },
  } = useReviewPhotos(id, 15);
  const [showAllPhotosModal, setShowAllPhotosModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReviewSheetStepped, setShowReviewSheetStepped] = useState(false);
  const [onFirstReview, setOnFirstReview] = useState(false);
  const [hasMoreReviews, setHasMoreReviews] = useState(true);
  const [currentReview, setCurrentReview] = useState<ReviewFragment | null>(null);
  const [currentReviewEdgeIndex, setCurrentReviewEdgeIndex] = useState(0);
  const [currentReviewPhotoIndex, setCurrentReviewPhotoIndex] = useState(0);

  const { hasNextPage } = data?.product?.reviews?.pageInfo ?? {};

  useEffect(() => {
    setCurrentReview(data?.product?.reviews?.edges?.[currentReviewEdgeIndex]?.review ?? null);
  }, [currentReviewEdgeIndex, data?.product?.reviews?.edges]);

  /**
   * Handles getting the next review in the data based on the current one selected by selectReview
   */
  const getNextReview = () => {
    const reviewEdges = data?.product?.reviews?.edges;

    if (
      reviewEdges &&
      (currentReviewEdgeIndex || currentReviewEdgeIndex === 0) &&
      currentReviewEdgeIndex < reviewEdges?.length - 1
    ) {
      setCurrentReviewEdgeIndex(currentReviewEdgeIndex + 1);
      setCurrentReviewPhotoIndex(0);
    } else {
      query.fetchMore({
        variables: { id, after: data?.product?.reviews?.edges?.[currentReviewEdgeIndex]?.cursor },
      });
    }
    trackInteractionEvent(REVIEW_IMAGE_GALLERY_EVENT_CATEGORY, 'view-review-button', 'next');
  };

  /**
   * Handles getting the previous review in the data based on the current one selected by selectReview
   */
  const getPrevReview = () => {
    if ((currentReviewEdgeIndex || currentReviewEdgeIndex === 0) && currentReviewEdgeIndex > 0) {
      setCurrentReviewEdgeIndex(currentReviewEdgeIndex - 1);
      setCurrentReviewPhotoIndex(0);
      trackInteractionEvent(REVIEW_IMAGE_GALLERY_EVENT_CATEGORY, 'view-review-button', 'previous');
    }
  };

  /**
   * Handles getting a review based on the image id passed
   * @param id string id from ReviewPhotos made up of reviewId and index of the photo in the array
   */
  const selectReview: SelectReview = (id) => {
    const reviewId = id.split('_')[0];
    const photoIndex = id.split('_')[1];
    const reviewEdgeIndex =
      data?.product?.reviews?.edges?.findIndex((review) => {
        return review?.review?.id === reviewId;
      }) ?? 0;
    if (reviewEdgeIndex < 0) {
      getNextReview();
    } else {
      setCurrentReviewEdgeIndex(reviewEdgeIndex);
    }
    if (photoIndex) {
      const index = Number.parseInt(photoIndex);
      setCurrentReviewPhotoIndex(index);
      return { reviewId, photoIndex };
    }
  };

  /**
   * sets onFirstReview flag
   * Reacts to currentReviewEdgeIndex changes by denoting if it's the first in list of reviews
   */
  useEffect(() => {
    if (currentReviewEdgeIndex === 0) setOnFirstReview(true);
    else setOnFirstReview(false);
  }, [currentReviewEdgeIndex, data?.product?.reviews?.edges]);

  /**
   * sets hasMoreReviews flag
   * Reacts to currentReviewEdgeIndex changes by denoting if it's the last available review in the data and there's no more reviews to load
   */
  useEffect(() => {
    const reviewEdges = data?.product?.reviews?.edges;

    if (
      reviewEdges &&
      (currentReviewEdgeIndex || currentReviewEdgeIndex === 0) &&
      currentReviewEdgeIndex === reviewEdges?.length - 1 &&
      !hasNextPage
    ) {
      setHasMoreReviews(false);
    } else {
      setHasMoreReviews(true);
    }
  }, [currentReviewEdgeIndex, data?.product?.reviews?.edges, hasNextPage]);

  return (
    <ReviewPhotoContext.Provider
      value={{
        currentReview,
        currentReviewPhotoIndex,
        setCurrentReviewEdgeIndex,
        fetchMore,
        getNextReview,
        getPrevReview,
        hasMoreImages,
        hasMoreReviews,
        images,
        loading,
        onFirstReview,
        selectReview,
        showAllPhotosModal,
        setShowAllPhotosModal,
        showReviewModal,
        setShowReviewModal,
        showReviewSheetStepped,
        setShowReviewSheetStepped,
      }}
    >
      {children}
    </ReviewPhotoContext.Provider>
  );
};

export const useReviewPhotoContext = () => useContext(ReviewPhotoContext);
